import { Cell, CellType, DungeonConfig } from '../types';

export class Dungeon {
  readonly width: number;
  readonly height: number;
  readonly cellSize: number;
  private cells: Cell[][];

  constructor(config: DungeonConfig) {
    this.width = config.width;
    this.height = config.height;
    this.cellSize = config.cellSize;
    this.cells = this.createEmptyGrid();
  }

  private createEmptyGrid(): Cell[][] {
    const grid: Cell[][] = [];
    for (let z = 0; z < this.height; z++) {
      const row: Cell[] = [];
      for (let x = 0; x < this.width; x++) {
        row.push({ type: CellType.SOLID });
      }
      grid.push(row);
    }
    return grid;
  }

  getCell(x: number, z: number): Cell | null {
    if (x < 0 || x >= this.width || z < 0 || z >= this.height) {
      return null;
    }
    return this.cells[z][x];
  }

  setCell(x: number, z: number, type: CellType): void {
    if (x >= 0 && x < this.width && z >= 0 && z < this.height) {
      this.cells[z][x].type = type;
    }
  }

  isWalkable(x: number, z: number): boolean {
    const cell = this.getCell(x, z);
    return cell !== null && cell.type === CellType.OPEN;
  }

  isWalkableWorld(worldX: number, worldZ: number): boolean {
    const cellX = Math.floor(worldX / this.cellSize);
    const cellZ = Math.floor(worldZ / this.cellSize);
    return this.isWalkable(cellX, cellZ);
  }

  worldToCell(worldX: number, worldZ: number): { x: number; z: number } {
    return {
      x: Math.floor(worldX / this.cellSize),
      z: Math.floor(worldZ / this.cellSize)
    };
  }

  cellToWorld(cellX: number, cellZ: number): { x: number; z: number } {
    return {
      x: (cellX + 0.5) * this.cellSize,
      z: (cellZ + 0.5) * this.cellSize
    };
  }

  findOpenCell(): { x: number; z: number } | null {
    for (let z = 1; z < this.height - 1; z++) {
      for (let x = 1; x < this.width - 1; x++) {
        if (this.isWalkable(x, z)) {
          return { x, z };
        }
      }
    }
    return null;
  }

  // Find the yaw angle to face an adjacent open cell from the given position
  // Returns angle in radians, or 0 if no adjacent open cell found
  findOpenDirection(cellX: number, cellZ: number): number {
    // Check cardinal directions: North (-Z), East (+X), South (+Z), West (-X)
    // Yaw: 0 = -Z, PI/2 = -X, PI = +Z, -PI/2 = +X
    const directions = [
      { dx: 0, dz: -1, yaw: 0 },           // North
      { dx: 1, dz: 0, yaw: -Math.PI / 2 }, // East
      { dx: 0, dz: 1, yaw: Math.PI },      // South
      { dx: -1, dz: 0, yaw: Math.PI / 2 }, // West
    ];

    for (const dir of directions) {
      if (this.isWalkable(cellX + dir.dx, cellZ + dir.dz)) {
        return dir.yaw;
      }
    }

    return 0;
  }
}
